// HealthSherpaEmparejarView — atar cada póliza de HealthSherpa con la del CRM.
//
// POR QUE ESTO ES UNA PANTALLA Y NO UN BOTÓN DE "EMPAREJAR TODO".
// HealthSherpa **no manda ningún dato personal**: ni nombre, ni fecha de
// nacimiento, ni prima. Lo único comparable con el CRM es la aseguradora y la
// fecha de efecto. Eso alcanza cuando la fecha es rara (una póliza efectiva el
// 1 de abril suele ser única), y NO alcanza en el caso más común de todos:
// media cartera es efectiva el 1 de enero con el mismo carrier.
//
// Así que el servidor PROPONE y la persona CONFIRMA. Las propuestas seguras
// vienen premarcadas; las dudosas, no, y hay que elegir. Un enlace equivocado
// hace que el estado de la póliza de un cliente pise la de otro en silencio,
// y eso no se descubre hasta que se llama al cliente equivocado.
//
// Se empareja una vez por póliza. Después, esa póliza ya no vuelve a salir.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, Link2, Check, Copy, AlertTriangle, HeartPulse, Unlink, Save } from "lucide-react";
import {
  getHealthSherpaEmparejar,
  saveHealthSherpaEmparejar,
  unlinkHealthSherpaPolicy,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";
import { toast } from "./Toast.jsx";

const dia = (v) => {
  if (!v) return "—";
  // Partes LOCALES, nunca toISOString: en UTC el día se corre de madrugada.
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
};

export function HealthSherpaEmparejarView({ lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  // hsPolicyId -> policyId elegido ("" = ninguno).
  const [eleccion, setEleccion] = useState({});

  const cargar = useCallback(async () => {
    setCargando(true); setError("");
    try {
      const d = await getHealthSherpaEmparejar();
      setDatos(d);
      // Las SEGURAS vienen premarcadas; las dudosas se dejan en blanco a
      // propósito, para que elegir sea un acto y no un descuido.
      const inicial = {};
      for (const p of d.propuestas || []) {
        const segura = (p.candidatas || []).find((c) => c.segura);
        if (segura) inicial[p.hsPolicyId] = segura.policyId;
      }
      setEleccion(inicial);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Las que ya están atadas no se vuelven a ofrecer: salen aparte, abajo.
  const yaAtadas = useMemo(
    () => (datos?.polizas || []).filter((p) => p.hsPolicyId),
    [datos],
  );
  const pendientes = useMemo(
    () => (datos?.propuestas || []).filter((p) => !yaAtadas.some((q) => q.hsPolicyId === p.hsPolicyId)),
    [datos, yaAtadas],
  );

  const porElegir = pendientes.filter((p) => eleccion[p.hsPolicyId]).length;

  const card = {
    background: "var(--c-card,#0f2433)", border: "1px solid var(--c-border,#1c3a4f)",
    borderRadius: 12, padding: "1.1rem", marginBottom: "1rem",
  };
  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px",
    background: bg, color: fg, border: "none", borderRadius: 8, fontSize: 14,
    fontWeight: 600, cursor: guardando ? "wait" : "pointer", opacity: guardando ? 0.7 : 1,
  });
  const help = { fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 5 };

  const guardar = async () => {
    const enlaces = pendientes
      .filter((p) => eleccion[p.hsPolicyId])
      .map((p) => ({
        policyId: eleccion[p.hsPolicyId],
        hsPolicyId: p.hsPolicyId,
        hsConfirmationId: p.hsConfirmationId,
      }));
    if (!enlaces.length) return;
    setGuardando(true);
    try {
      const r = await saveHealthSherpaEmparejar(enlaces);
      toast.success(t(`${r.guardados} emparejadas.`, `${r.guardados} linked.`));
      await cargar();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setGuardando(false);
    }
  };

  const desatar = async (p) => {
    const ok = await confirmDialog({
      message: t(
        `Se deshace el enlace de la póliza de ${p.cliente || "este cliente"}. Su estado deja de actualizarse solo. La póliza y el cliente NO se tocan. ¿Continuar?`,
        `This unlinks ${p.cliente || "this client"}'s policy. Its status stops updating on its own. The policy and the client are NOT touched. Continue?`,
      ),
    });
    if (!ok) return;
    try {
      await unlinkHealthSherpaPolicy(p.id);
      toast.success(t("Enlace deshecho.", "Link removed."));
      await cargar();
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <HeartPulse size={22} color="#00d4ff" />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          {t("Emparejar con HealthSherpa", "Match with HealthSherpa")}
        </h2>
      </div>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: 13.5, marginTop: 0, marginBottom: "1.25rem", maxWidth: 680 }}>
        {t(
          "Se hace una vez por póliza. Después, su estado se actualiza solo y esta pantalla deja de pedírtelo.",
          "Once per policy. After that its status updates on its own and this screen stops asking.",
        )}
      </p>

      {/* POR QUE HAY QUE HACER ESTO A MANO. Si no se dice, parece que Deskte
          no sabe hacerlo solo por dejadez. */}
      <div style={{ ...card, background: "rgba(122,163,192,.08)" }}>
        <div style={{ fontSize: 12.8, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.6 }}>
          {t(
            "HealthSherpa no manda nombres: ni del cliente, ni fecha de nacimiento, ni prima. Solo manda la aseguradora y la fecha de efecto. Por eso Deskte puede proponer, pero no decidir — y emparejar mal haría que el estado de un cliente apareciera en la póliza de otro.",
            "HealthSherpa sends no names: no client name, no date of birth, no premium. Only the carrier and the effective date. That's why Deskte can propose but not decide — and a wrong match would show one client's status on another client's policy.",
          )}
        </div>
      </div>

      {error && <div style={{ ...card, background: "#3a1620", borderColor: "#7f1d1d", color: "#fecaca" }}>{error}</div>}

      {cargando ? (
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={16} className="dkt-spin" /> {t("Leyendo tus pólizas de HealthSherpa…", "Reading your HealthSherpa policies…")}
        </div>
      ) : (
        <>
          {datos?.frenados && (
            <div style={{ ...card, background: "rgba(251,191,36,.08)", borderColor: "rgba(251,191,36,.3)", fontSize: 13 }}>
              {t(
                "HealthSherpa nos frenó por número de consultas, así que esta lista está incompleta. Vuelve en un rato para ver el resto.",
                "HealthSherpa rate-limited us, so this list is incomplete. Come back in a little while for the rest.",
              )}
            </div>
          )}

          {pendientes.length === 0 ? (
            <div style={{ ...card, textAlign: "center", color: "var(--c-text2,#7aa3c0)", fontSize: 13.5 }}>
              {t(
                "No queda nada por emparejar. Si acabas de vender una póliza nueva, aparecerá aquí cuando HealthSherpa la publique.",
                "Nothing left to match. If you just sold a new policy, it'll show up here once HealthSherpa publishes it.",
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 13, color: "var(--c-text2,#7aa3c0)" }}>
                  {t(
                    `${pendientes.length} por emparejar · ${porElegir} elegidas`,
                    `${pendientes.length} to match · ${porElegir} chosen`,
                  )}
                </div>
                <button onClick={guardar} disabled={guardando || !porElegir} style={{ ...btn(porElegir ? "#16a34a" : "transparent", porElegir ? "#fff" : "#45688a") }}>
                  {guardando ? <Loader2 size={15} className="dkt-spin" /> : <Save size={15} />}
                  {t("Guardar las elegidas", "Save the chosen ones")}
                </button>
              </div>

              {pendientes.map((p) => {
                const suya = (datos.deHS || []).find((h) => h.hsPolicyId === p.hsPolicyId);
                return (
                  <div key={p.hsPolicyId} style={card}>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "baseline", marginBottom: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {suya?.carrier || t("Aseguradora desconocida", "Unknown carrier")}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--c-text2,#7aa3c0)" }}>
                        {t("Efectiva", "Effective")} {dia(suya?.effectiveDate)}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono,monospace)" }}>
                        #{p.hsPolicyId}
                      </div>
                    </div>

                    <ElegirPoliza
                      hsPolicyId={p.hsPolicyId}
                      hsConfirmationId={p.hsConfirmationId}
                      candidatas={p.candidatas}
                      polizas={datos.polizas || []}
                      elegida={eleccion[p.hsPolicyId] || ""}
                      onElegir={(policyId) => setEleccion((e) => ({ ...e, [p.hsPolicyId]: policyId }))}
                      lang={lang}
                    />
                  </div>
                );
              })}
            </>
          )}

          {yaAtadas.length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                {t(`Ya emparejadas (${yaAtadas.length})`, `Already matched (${yaAtadas.length})`)}
              </div>
              <div style={{ ...help, marginBottom: 10 }}>
                {t(
                  "Su estado se actualiza solo. Si alguna quedó mal emparejada, deshazla aquí.",
                  "Their status updates on its own. If one was matched wrong, undo it here.",
                )}
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {yaAtadas.map((p) => (
                  <div key={p.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap",
                    padding: "8px 11px", borderRadius: 8, background: "var(--c-surface2,#0b1c2b)",
                    border: "1px solid var(--c-border,#1c3a4f)", fontSize: 13,
                  }}>
                    <span>
                      <Link2 size={13} style={{ verticalAlign: -2, marginRight: 6, color: "#4ade80" }} />
                      <b>{p.cliente || t("(sin cliente)", "(no client)")}</b>
                      <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
                        {" · "}{p.carrier} · {t("efectiva", "effective")} {dia(p.effectiveDate)}
                      </span>
                    </span>
                    <button onClick={() => desatar(p)} style={{ ...btn("transparent", "#f87171"), padding: "5px 9px", fontSize: 12.5 }}>
                      <Unlink size={13} /> {t("Deshacer", "Undo")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lo que todavía NO pasa. Sin esto, alguien empareja y se queda
              esperando que las pólizas cambien solas esa misma tarde. */}
          <div style={{ ...card, background: "transparent", borderStyle: "dashed" }}>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <AlertTriangle size={16} color="#7aa3c0" style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.6 }}>
                {t(
                  "Emparejar todavía NO cambia el estado de tus pólizas: solo deja atado qué es qué. Traer el estado es el siguiente paso y pasará por revisión antes de tocar nada, porque HealthSherpa avisa de que su servicio está en pruebas y puede mandar datos incompletos.",
                  "Matching does NOT change your policy status yet: it only records what is what. Bringing the status in is the next step and will go through review before touching anything, because HealthSherpa warns their service is in alpha and can send partial data.",
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Elegir con qué póliza del CRM se empareja una de HealthSherpa.
 *
 *  POR QUE HAY BUSCADOR Y NO SOLO PROPUESTAS. La primera versión de esta
 *  pantalla solo ofrecía lo que el servidor conseguía adivinar con la
 *  aseguradora y la fecha de efecto, y cuando no adivinaba decía "ninguna de
 *  tus pólizas se parece" y ahí se acababa. A Lissette le dio **cero** en las
 *  21, y con razón: HealthSherpa no manda nombres, así que adivinar casi
 *  nunca va a funcionar.
 *
 *  Lo que de verdad hace falta es poder elegir. La agente abre su portal de
 *  HealthSherpa, busca el número de confirmación que sale aquí arriba, ve de
 *  quién es, y escribe ese nombre en el buscador. Las propuestas se quedan
 *  como atajo para cuando sí aciertan, no como única salida. */
function ElegirPoliza({ hsPolicyId, hsConfirmationId, candidatas, polizas, elegida, onElegir, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [busca, setBusca] = useState("");
  const [copiado, setCopiado] = useState(false);

  const libres = useMemo(() => polizas.filter((p) => !p.hsPolicyId), [polizas]);
  const idsPropuestos = useMemo(() => new Set(candidatas.map((c) => c.policyId)), [candidatas]);

  const resultados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return [];
    return libres
      .filter((p) => `${p.cliente || ""} ${p.carrier || ""} ${p.planName || ""} ${p.policyNumber || ""}`
        .toLowerCase().includes(q))
      .slice(0, 25);
  }, [busca, libres]);

  const fila = (p, atajo) => {
    const marcada = elegida === p.id;
    return (
      <label key={p.id} style={{
        display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
        padding: "9px 11px", borderRadius: 8,
        background: marcada ? "rgba(74,222,128,.08)" : "var(--c-surface2,#0b1c2b)",
        border: `1px solid ${marcada ? "#4ade80" : "var(--c-border,#1c3a4f)"}`,
      }}>
        <input type="radio" name={`hs-${hsPolicyId}`} checked={marcada}
          onChange={() => onElegir(p.id)} style={{ marginTop: 3 }} />
        <span style={{ fontSize: 13, lineHeight: 1.5 }}>
          <b>{p.cliente || t("(sin cliente)", "(no client)")}</b>
          <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
            {" · "}{p.carrier}{p.planName ? ` · ${p.planName}` : ""}
            {p.policyNumber ? ` · #${p.policyNumber}` : ""}
          </span>
          {atajo && (
            <span style={{ color: "#4ade80", fontSize: 11.5, display: "block" }}>
              {t("Deskte cree que es esta", "Deskte thinks this is the one")}
            </span>
          )}
        </span>
      </label>
    );
  };

  const inp = {
    width: "100%", padding: "8px 11px", borderRadius: 8, fontSize: 13.5,
    background: "var(--c-surface2,#0b1c2b)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1c3a4f)", boxSizing: "border-box",
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {/* EL NUMERO DE CONFIRMACION, a mano y copiable. Es lo que la agente
          busca en su portal de HealthSherpa para saber de quién es esta
          póliza: sin él, esta pantalla le pide adivinar a ella también. */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12.5 }}>
        <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
          {t("Búscala en HealthSherpa por este número:", "Look it up in HealthSherpa by this number:")}
        </span>
        <code style={{ fontFamily: "var(--font-mono,monospace)", fontSize: 13, color: "var(--c-white,#eef6ff)" }}>
          {hsConfirmationId}
        </code>
        <button
          type="button"
          onClick={() => {
            try {
              navigator.clipboard.writeText(hsConfirmationId);
              setCopiado(true);
              setTimeout(() => setCopiado(false), 1500);
            } catch { /* se ve igual al lado */ }
          }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px",
            background: "transparent", color: "#00d4ff", border: "1px solid var(--c-border,#1c3a4f)",
            borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          {copiado ? <Check size={12} /> : <Copy size={12} />}
          {copiado ? t("Copiado", "Copied") : t("Copiar", "Copy")}
        </button>
      </div>

      {candidatas.length > 0 && candidatas.map((c) => {
        const pol = polizas.find((x) => x.id === c.policyId);
        return pol ? fila(pol, c.segura) : null;
      })}

      <div>
        <input
          style={inp}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={t("Escribe el nombre del cliente…", "Type the client's name…")}
        />
        {busca.trim() && resultados.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--c-text3,#45688a)", marginTop: 6 }}>
            {t(
              "Ninguna póliza tuya coincide. Puede ser de un cliente que no está en Deskte, o que su póliza ya esté emparejada con otra.",
              "No policy of yours matches. It may be a client who isn't in Deskte, or their policy is already matched to another.",
            )}
          </div>
        )}
      </div>

      {resultados.filter((p) => !idsPropuestos.has(p.id)).map((p) => fila(p, false))}

      {/* Poder decir "ninguna" importa: sin esta opción, una propuesta
          premarcada que esté mal se guarda sola. */}
      <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer", padding: "4px 11px", fontSize: 12.5, color: "var(--c-text2,#7aa3c0)" }}>
        <input type="radio" name={`hs-${hsPolicyId}`} checked={!elegida}
          onChange={() => onElegir("")} />
        {t("Dejarla para después", "Leave it for later")}
      </label>
    </div>
  );
}
